import { useState } from 'react';
import styles from 'features/chatAssist/components/chatAssist.module.scss';
import ActionButtonTooltip from './ActionButtonTooltip';

import useSendFeedbackResponseMutation from 'features/chatAssist/newServices/mutations/useSendFeedbackResponse';
import useChangeResponseVersionMutation from 'features/chatAssist/newServices/mutations/useChangeResponseVersion';

const FeedbackSection = ({ isWaiting, handleRegenerateResponse = () => {}, clientId, conversationId, message }) => {
  const [currentFeedbackStatus, setCurrentFeedbackStatus] = useState(message?.feedback ?? null);

  const [showLikeTooltip, setShowLikeTooltip] = useState(false);
  const [showDisLikeTooltip, setShowDisLikeTooltip] = useState(false);
  const [showReGenerateTooltip, setShowReGenerateTooltip] = useState(false);

  const { mutate: submitFeedback, isLoading: isSendingFeedback } = useSendFeedbackResponseMutation();
  const { mutate: changeResponseVersion, isLoading: isChangingVersion } = useChangeResponseVersionMutation();

  const onClick = (type) => {
    // user only unselected like/dislike response
    if (type === 'like') {
      setCurrentFeedbackStatus((prevStatus) => {
        const newStatus = prevStatus === 'good' ? null : 'good';
        if (newStatus === null) {
          submitFeedback({
            conversationId,
            messageId: message.message_id,
            feedback: null,
          });
        }
        return newStatus;
      });
    }
    if (type === 'dislike') {
      setCurrentFeedbackStatus((prevStatus) => {
        const newStatus = prevStatus === 'bad' ? null : 'bad';
        if (newStatus === null)
          submitFeedback({
            conversationId,
            messageId: message.message_id,
            feedback: null,
          });
        return newStatus;
      });
    }
    type === 'like' ? setShowLikeTooltip(true) : setShowDisLikeTooltip(true);
  };

  const onCloseTooltip = async (type = '', reason) => {
    if (type === 'like') {
      submitFeedback({
        conversationId,
        messageId: message.message_id,
        feedback: 'good',
        reason,
      });
      setShowLikeTooltip(false);
    }
    if (type === 'dislike') {
      submitFeedback({
        conversationId,
        messageId: message.message_id,
        feedback: 'bad',
        reason,
      });
      setShowDisLikeTooltip(false);
    }
    if (type === 'regenerate') {
      setShowReGenerateTooltip(false);
      await handleRegenerateResponse({
        message_id: message.message_id,
        regenerate_type: 'try_again',
        reason,
      });
    }
  };

  const onChangeVersionNumber = (type) => {
    const newVersionNumber =
      type === 'increment'
        ? (message?.version_number ?? 1) + 1
        : (message?.version_number ?? 1) - 1;
    changeResponseVersion({
      clientId,
      conversationId,
      messageId: message.message_id,
      versionNumber: newVersionNumber,
    });
  };

  return (
    <div className={styles.feedbackContainer}>
      <button
        disabled={isSendingFeedback || isChangingVersion || isWaiting}
        className={styles.feedbackButton}
        aria-label="I like this response"
        onClick={() => onClick('like')}
      >
        <svg
          style={{
            ...(currentFeedbackStatus === 'good' && { fill: '#0494FD' }),
          }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 640 640"
        >
          <path d="M235.5 102.8C256.3 68 300.5 54 338 71.6L345.2 75.4C380 96.3 394 140.5 376.4 178L376.4 178L362.3 208L472 208L479.4 208.4C515.7 212.1 544 242.8 544 280C544 293.2 540.4 305.4 534.2 316C540.3 326.6 543.9 338.8 544 352C544 370.3 537.1 386.8 526 399.5C527.3 404.8 528 410.3 528 416C528 441.1 515.1 463 495.8 475.9C493.9 511.4 466.4 540.1 431.4 543.6L424 544L319.9 544C301.9 544 284 540.6 267.3 534.1L260.2 531.1L259.5 530.8L252.9 527.6L252.2 527.3L240 520.8C227.7 514.3 216.7 506.1 207.1 496.7C203 523.6 179.8 544.1 151.8 544.1L119.8 544.1C88.9 544.1 63.8 519 63.8 488.1L64 264C64 233.1 89.1 208 120 208L152 208C162.8 208 172.9 211.1 181.5 216.5L231.6 110L232.2 108.8L234.9 103.8L235.5 102.9zM120 256C115.6 256 112 259.6 112 264L112 488C112 492.4 115.6 496 120 496L152 496C156.4 496 160 492.4 160 488L160 264C160 259.6 156.4 256 152 256L120 256zM317.6 115C302.8 108.1 285.3 113.4 276.9 127L274.7 131L217.9 251.9C214.4 259.4 212.4 267.4 211.9 275.6L211.8 279.8L211.8 392.7L212 400.6C214.4 433.3 233.4 462.7 262.7 478.3L274.2 484.4L280.5 487.5C292.9 493.1 306.3 496 319.9 496L424 496L426.4 495.9C438.5 494.7 448 484.4 448 472L447.8 469.4C447.7 468.5 447.6 467.7 447.4 466.8C444.7 454.7 451.7 442.6 463.4 438.8C473.1 435.7 480 426.6 480 416C480 411.7 478.9 407.8 476.9 404.2C470.6 393.1 474.1 379 484.9 372.2C491.7 367.9 496.1 360.4 496.1 352C496.1 344.9 493 338.5 487.9 334C482.7 329.4 479.7 322.9 479.7 316C479.7 309.1 482.7 302.6 487.9 298C493 293.5 496.1 287.1 496.1 280L496 277.6C494.9 266.3 485.9 257.3 474.6 256.2L472.2 256.1L324.7 256.1C316.5 256.1 308.9 251.9 304.5 245C300.1 238.1 299.5 229.3 303 221.9L333 157.6C340 142.6 334.4 124.9 320.5 116.6L317.6 115z" />
        </svg>
        {showLikeTooltip && currentFeedbackStatus !== null && (
          <ActionButtonTooltip
            title="Thanks for the feedback. What could we improve?"
            placeholder="Write your response here (Optional)"
            onClose={(reason) => onCloseTooltip('like', reason)}
          />
        )}
      </button>
      <button
        disabled={isSendingFeedback || isChangingVersion || isWaiting}
        className={styles.feedbackButton}
        onClick={() => onClick('dislike')}
        aria-label="I don't like this response"
      >
        <svg
          style={{
            ...(currentFeedbackStatus === 'bad' && { fill: '#0494FD' }),
          }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 640 640"
        >
          <path d="M424 96L431.4 96.4C466.4 100 493.9 128.6 495.8 164.1C513.6 175.9 525.9 195.5 527.8 218L528 224C528 229.7 527.3 235.2 526 240.5C536.2 252 542.8 266.8 543.8 283.2L544 288C544 301.2 540.4 313.4 534.2 324C539.1 332.4 542.4 341.9 543.5 352L543.9 360C543.9 397.3 515.6 427.9 479.3 431.6L471.9 432L362.2 432L376.3 462L379.4 469.6C391.9 505.3 377.6 545.1 345.2 564.6L338 568.5C300.5 586.1 256.3 572.1 235.4 537.3L234.8 536.4L232.1 531.4L231.5 530.2L201.4 466.2C192 484 173.4 496.1 151.9 496.1L119.9 496.1C89 496.1 63.9 471 63.9 440.1L64 216C64 185.1 89.1 160 120 160L152 160C164.4 160 175.9 164.1 185.2 171C198.4 149.6 217.2 131.6 240.2 119.4L252.4 112.9L253.1 112.6L259.7 109.4L260.4 109.1L267.5 106.1C284.2 99.5 302 96.2 320.1 96.2L424 96zM319.9 144C307.9 144 296 146.3 284.8 150.6L280.1 152.6L274.8 155.2L274.8 155.2L262.6 161.7C233.4 177.2 214.3 206.6 211.9 239.3L211.7 247.3L211.7 360.2L211.8 364.3C212.3 372.5 214.3 380.5 217.8 388L274.6 508.9L276.7 512.7C285.1 526.4 302.7 531.8 317.5 524.9L320.4 523.3C333.4 515.5 339.1 499.6 334.1 485.3L332.9 482.3L302.7 418.1C299.2 410.7 299.8 402 304.2 395C308.6 388 316.2 383.9 324.4 383.9L471.9 383.9L474.3 383.8C485.6 382.7 494.6 373.7 495.7 362.4L495.8 359.9C495.8 352.8 492.7 346.4 487.6 341.9C482.4 337.3 479.4 330.8 479.4 323.9C479.4 317 482.4 310.5 487.6 305.9C492 302 495 296.6 495.6 290.6L495.8 287.9C495.8 279.5 491.4 272 484.6 267.7C473.9 260.8 470.4 246.8 476.6 235.7C478.1 233.1 479.1 230.1 479.5 227.1L479.7 223.9C479.7 213.3 472.8 204.3 463.1 201.1C451.4 197.3 444.4 185.2 447.1 173.1C447.3 172.2 447.4 171.3 447.5 170.5L447.7 167.9C447.7 155.5 438.2 145.3 426.1 144.1L424 144L319.9 144zM120 208C115.6 208 112 211.6 112 216L112 440C112 444.4 115.6 448 120 448L152 448C156.4 448 160 444.4 160 440L160 216C160 211.6 156.4 208 152 208L120 208z" />
        </svg>
        {showDisLikeTooltip && currentFeedbackStatus !== null && (
          <ActionButtonTooltip
            title="Thanks for the feedback. What could we improve?"
            placeholder="Write your response here (Optional)"
            onClose={(reason) => onCloseTooltip('dislike', reason)}
          />
        )}
      </button>
      <button
        disabled={isSendingFeedback || isChangingVersion || isWaiting}
        className={styles.feedbackButton}
        aria-label="Re-generate response"
        onClick={() => setShowReGenerateTooltip(true)}
      >
        <svg
          style={{
            height: '14px',
            ...(currentFeedbackStatus === 'regenerate' && { fill: '#0494FD' }),
          }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 640 640"
        >
          <path d="M500.7 138.7L512 149.4L512 96C512 78.3 526.3 64 544 64C561.7 64 576 78.3 576 96L576 224C576 241.7 561.7 256 544 256L416 256C398.3 256 384 241.7 384 224C384 206.3 398.3 192 416 192L463.9 192L456.3 184.8C456.1 184.6 455.9 184.4 455.7 184.2C380.7 109.2 259.2 109.2 184.2 184.2C109.2 259.2 109.2 380.7 184.2 455.7C259.2 530.7 380.7 530.7 455.7 455.7C463.9 447.5 471.2 438.8 477.6 429.6C487.7 415.1 507.7 411.6 522.2 421.7C536.7 431.8 540.2 451.8 530.1 466.3C521.6 478.5 511.9 490.1 501 501C401 601 238.9 601 139 501C39.1 401 39 239 139 139C238.9 39.1 400.7 39 500.7 138.7z" />
        </svg>
        {showReGenerateTooltip && (
          <ActionButtonTooltip
            title="Regenerating the Answer. What could we Improve??"
            placeholder="Write your response here (Optional)"
            onClose={() => onCloseTooltip('regenerate')}
          />
        )}
      </button>
      <div className={styles.versionControlsContainer}>
        {message?.content && message?.version_number > 1 && (
          <button disabled={isSendingFeedback || isChangingVersion || isWaiting} onClick={() => onChangeVersionNumber('decrement')} className={styles.versionControlBtn}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
              <path d="M201.4 297.4C188.9 309.9 188.9 330.2 201.4 342.7L361.4 502.7C373.9 515.2 394.2 515.2 406.7 502.7C419.2 490.2 419.2 469.9 406.7 457.4L269.3 320L406.6 182.6C419.1 170.1 419.1 149.8 406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3L201.3 297.3z" />
            </svg>
          </button>
        )}
        <p>
          {message?.version_number ?? 1}/{message?.total_versions ?? 1}
        </p>
        {message?.version_number < message?.total_versions && (
          <button disabled={isSendingFeedback || isChangingVersion || isWaiting} onClick={() => onChangeVersionNumber('increment')} className={styles.versionControlBtn}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
              <path d="M439.1 297.4C451.6 309.9 451.6 330.2 439.1 342.7L279.1 502.7C266.6 515.2 246.3 515.2 233.8 502.7C221.3 490.2 221.3 469.9 233.8 457.4L371.2 320L233.9 182.6C221.4 170.1 221.4 149.8 233.9 137.3C246.4 124.8 266.7 124.8 279.2 137.3L439.2 297.3z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default FeedbackSection;
